import { HearingParticipantsSectionComponent, HearingAttendee, HearingAttendanceChange } from "./hearing-participants-section.component";
import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, Output, Signal, WritableSignal } from "@angular/core";
import { Validators } from "@angular/forms";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { EzFormInputType, InputChangeEvent, InputConfig } from "@ez-library/types/ez-form.types";
import { TerminologyIdentityDirective } from "@app/shared/terminology/terminology-identity.directive";
import { createTerminologyUsageSignals } from "@app/shared/terminology/terminology-usage-signals";
import { HEARING_DISCUSSION_TERMINOLOGY } from "./hearing-discussion.terminology";
import { ACCEPTANCE_OPTIONS, AtHearingFormValues, HearingRecordOffense } from "../hearing-record.models";

const narrative = (name: string, label: string, row: number, rows = 4): InputConfig => ({
  name, label, row, type: EzFormInputType.TEXTAREA, width: "fill", textAreaRows: rows, floatLabel: "always",
});

/** Shared capture controls beside expandable offense reference material. */
@Component({
  selector: "phms-hearing-offense-section",
  standalone: true,
  imports: [HearingParticipantsSectionComponent, CommonModule, UiLibraryModule, TerminologyIdentityDirective],
  templateUrl: "./hearing-offense-section.component.html",
  styleUrl: "./hearing-offense-section.component.scss",
})
export class HearingOffenseSectionComponent {
  @Input() attendees: readonly HearingAttendee[] = [];
  @Input() rosterLoading = false;
  @Input() rosterMessage = "";
  @Input() presence?: Signal<Record<string, boolean>>;
  @Input() participationConfig: InputConfig[] = [];
  @Input() proceedingId: number | null = null;
  @Input() canReadContacts = false;
  @Output() attendanceChange = new EventEmitter<HearingAttendanceChange>();
  @Input() testId = "";
  @Input() values!: WritableSignal<AtHearingFormValues>;
  @Input() canEdit!: Signal<boolean>;
  @Input() offenses!: Signal<HearingRecordOffense[]>;
  @Input() ageAtOffense!: Signal<number | null>;
  @Input() priorHearingOutcome!: Signal<string>;
  @Output() inputChange = new EventEmitter<InputChangeEvent>();
  readonly terminologyUsage = HEARING_DISCUSSION_TERMINOLOGY;
  readonly text = createTerminologyUsageSignals(this.terminologyUsage);
  readonly offenseConfig: InputConfig[] = [
    { ...narrative("offenseCircumstances", "Circumstances of the offense *", 1),
      validation: [Validators.required, Validators.pattern(/\S/)],
      validationMessages: { required: "Enter the circumstances of the offense.", pattern: "Enter the circumstances of the offense." } },
    { name: "offenseMotivation", label: "Motivation for the crime", type: EzFormInputType.TEXT, row: 2, width: "fill", floatLabel: "always" },
    { name: "offenseUnderStress", label: "Crime committed under significant stress", type: EzFormInputType.CHECKBOX, row: 3 },
    narrative("offenseNexus", "Nexus: offense to current dangerousness", 4, 3),
  ];
  readonly insightConfig: InputConfig[] = [
    narrative("insightCausativeFactors", "Causative factors", 1),
    { name: "acceptanceOfResponsibility", label: "Acceptance of responsibility", type: EzFormInputType.RADIO_GROUP_PLAIN,
      options: [...ACCEPTANCE_OPTIONS], row: 2, width: "fill" },
    { name: "signsOfRemorse", label: "Signs of remorse", type: EzFormInputType.CHECKBOX, row: 3 },
    narrative("remorseVictimImpact", "Victim impact acknowledgment", 4, 3),
  ];

  controllingOffense(): HearingRecordOffense | null {
    const offenses = this.offenses();
    return offenses.find((offense) => /controlling/i.test(offense.summary ?? ""))
      ?? offenses.find((offense) => Boolean(offense.statuteReference)) ?? offenses[0] ?? null;
  }
  onInputChange(event: InputChangeEvent): void {
    if (!this.canEdit() || ![...this.offenseConfig, ...this.insightConfig].some(field => field.name === event.name)) return;
    const name = event.name as keyof AtHearingFormValues;
    const value = name === "offenseUnderStress" || name === "signsOfRemorse" ? event.value === true : String(event.value ?? "");
    // Merge only the changed field; each form may still contain an older copy of its sibling's values.
    this.values.update(current => ({ ...current, [name]: value }));
    this.inputChange.emit({ name: String(name), value });
  }
}
